'use strict';

require('./chat.scss');

const SV = require('server-vars');
const get = require('lodash.get');
const moment = require('moment');
const enabler = require('../../utils/enabler');
const FavoriteModel = require('bunsen/models/identity/Favorite');
const {sessionStorage} = require('dibs-browser-storage');
const root = require('bunsen/helpers/windowCheck');

const isPrintPDP = SV.get('settings.isPrintPDP');
const isEnabled = enabler.isEnabled('chat_intercom') && !isPrintPDP;
const widgetLoader = require('../../widgets/widgetLoader');

const {isPageType} = require('../../notifications/pageTypeHelper');
const {PAGE_TYPE, BROWSE_TYPES} = require('../../constants/pageTypes');
const User = require('../../models/User');

const {
    URL: {
        queryKey: {tradePromoDate}
    }
} = require('bunsen/helpers/uri');

const CHAT_STARTED_STORAGE_KEY = '__chatStarted';
const ARGUMENTS_STORAGE_KEY = '__chatArguments';
const CHECKOUT_UNLOAD_KEY = '__checkoutUnload';
const CHECKOUT_TYPE_KEY = '__checkoutTransactionType';

const TRADE_PROMO_DATE_FROM = tradePromoDate || '2017-01-16';

const PAGE_TYPE_WHITELIST = [
    BROWSE_TYPES,
    PAGE_TYPE.CHECKOUT,
    PAGE_TYPE.FAVORITES, // Cart + Favorites has same pageType
    PAGE_TYPE.HOME,
    PAGE_TYPE.INVOICE,
    PAGE_TYPE.PDP,
    PAGE_TYPE.SEARCH
];

function getDate() {
    const date = new Date();
    return moment(date).format();
}

function getIntercomDate(date) {
    date = new Date(date);
    return date.getTime() / 1000; // get UTC seconds
}

function applyArgumentQueue(intercom) {
    const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];

    if (argumentQueue.length) {
        argumentQueue.forEach(args => {
            intercom.apply(intercom, args);
        });
    }

    sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, []);
}

function load(...args) {
    const chatStarted = sessionStorage.getItem(CHAT_STARTED_STORAGE_KEY);
    const pageTypeValid = isPageType(PAGE_TYPE_WHITELIST);

    if (chatStarted && isEnabled && pageTypeValid) {
        return widgetLoader.load('intercom').then(intercom => {
            applyArgumentQueue(intercom);
            if (args.length) {
                intercom.apply(intercom, args);
            }
        });
    } else if (isEnabled) {
        const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];
        argumentQueue.push(args);
        sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, argumentQueue);
    }

    // if `chat_intercom` is not enabled resolve
    return Promise.resolve();
}

function trackEvent(...args) {
    return load('trackEvent', ...args);
}

function update(data) {
    return load('update', data);
}

function getUserTestGroup({user, tradeUserCreateDate}) {
    const userId = user.id;
    const lastIdNumber = Number.parseInt(userId.slice(-1));
    const testGroupData = {};

    // User ids end up with 1 or 2, ~20% of users
    if ([1, 2].indexOf(lastIdNumber) > -1) {
        testGroupData.user_test_group = 'Holdout'; // eslint-disable-line camelcase
    }

    testGroupData.user_test_group = 'Test'; // eslint-disable-line camelcase

    if (tradeUserCreateDate) {
        const secontToLastIdNumber = Number.parseInt(userId.slice(-2, -1));
        if ([4, 5].indexOf(secontToLastIdNumber) > -1 && moment(tradeUserCreateDate) > moment(TRADE_PROMO_DATE_FROM)) {
            testGroupData.user_trade_promo_group = 'Test'; // eslint-disable-line camelcase
        } else {
            testGroupData.user_trade_promo_group = 'Control'; // eslint-disable-line camelcase
        }
    }

    return testGroupData;
};

const chat = {
    start() {
        sessionStorage.setItem(CHAT_STARTED_STORAGE_KEY, true);
        return load();
    },

    show() {
        return load('show');
    },

    fillUserData({user, tradeSalesRep, tradeUserCreateDate, vipSalesRep}) {
        if (!User.loggedIn()) {
            return Promise.resolve();
        }

        const userType = User.getCustomerType();
        const {id, createdDate, profile: {firstName, lastName, email}} = user;

        const userData = Object.assign({
            'user_id': id,
            name: `${firstName || ''} ${lastName || ''}`.trim(),
            email: email,
            'user_type': userType,
            'registered_at': getIntercomDate(createdDate),
            'trade_account_manager': get(tradeSalesRep, 'email'),
            'vip_account_manager': get(vipSalesRep, 'email')
        }, getUserTestGroup({user, tradeUserCreateDate}));

        return update(userData);
    },

    show() {
        return load('show');
    },

    customAttribute: {
        transactionsStatus({buyerStatus, totalTransactions}) {
            return update({
                'buyer_status': buyerStatus,
                'total_transactions': totalTransactions
            });
        },

        tradeStatus(status) {
            return update({'trade_status': status});
        },

        verifiedTradeDate(date) {
            return update({'verified_trade_at': getIntercomDate(date)});
        },

        itemsInCart(itemCount) {
            return update({'items_in_cart': itemCount});
        },

        registeredOnPage() {
            const {pathname} = document.location;
            return update({'registered_on_page': pathname});
        }
    },

    track: {
        abandonFavorite(type) {
            switch (type) {
                case FavoriteModel.TYPE_SEARCH:
                    return trackEvent('abandon_follow_search');

                case FavoriteModel.TYPE_DEALER:
                    return trackEvent('abandon_follow_dealer');
            }

            return Promise.resolve();
        },

        abandonRequestNetPrice() {
            return trackEvent('abandon_request_net_price', {
                'fired_at': getDate()
            });
        },

        requestNetPrice() {
            return trackEvent('success_request_net_price', {
                'fired_at': getDate()
            });
        },

        abandonMakeOffer() {
            return trackEvent('make_offer_abandon', {
                'fired_at': getDate()
            });
        },

        makeOfferComplete() {
            return trackEvent('make_offer_complete', {
                'fired_at': getDate()
            });
        },

        abandonContactDealer() {
            return trackEvent('abandon_contact_dealer', {
                'fired_at': getDate()
            });
        },

        contactDealer() {
            return trackEvent('contact_dealer', {
                'fired_at': getDate()
            });
        },

        addToCart() {
            return trackEvent('item_added_to_cart', {
                'fired_at': getDate()
            });
        },

        itemSaved() {
            return trackEvent('success_save_item');
        },

        shippingDetailsClick() {
            return trackEvent('view_shipping_details');
        },

        buyerAssuranceClick() {
            return trackEvent('buyer_protection');
        },

        checkoutReached(data, isNegotiable) {
            sessionStorage.setItem(CHECKOUT_TYPE_KEY, {isNegotiable});
            return trackEvent('checkout_reached', data);
        },

        checkoutComplete() {
            return trackEvent('checkout_completed');
        },

        checkoutAbandon() {
            return trackEvent('checkout_abandon');
        },

        checkoutSurveySubmitted(data) {
            return trackEvent('checkout_survey_submitted', data);
        },

        tradeBasicInfoCompleted(noWebsite) {
            const prefix = noWebsite ? 'no ' : '';
            return trackEvent(`trade application step 1 - ${prefix}website`);
        }
    }

};

function checkAbandonCheckout() {
    const {pathname} = document.location;
    const isCheckoutPage = pathname.indexOf('checkout') > -1;
    const isCheckoutConfirmationPage = pathname.indexOf('checkout/confirmation') > -1;
    const isMyOrdersPage = pathname.indexOf('my/orders') > -1;

    if (isCheckoutPage && !isCheckoutConfirmationPage) {
        root.onbeforeunload = function () {
            sessionStorage.setItem(CHECKOUT_UNLOAD_KEY, true);
        };
    } else if (!isCheckoutConfirmationPage && !isMyOrdersPage && sessionStorage.getItem(CHECKOUT_UNLOAD_KEY)) {
        const isNegotiable = get(sessionStorage.getItem(CHECKOUT_TYPE_KEY), 'isNegotiable');

        if (isNegotiable) {
            chat.track.abandonMakeOffer();
        } else {
            chat.track.checkoutAbandon();
        }
    }

    sessionStorage.deleteItem(CHECKOUT_UNLOAD_KEY);
};

function checkConductSearch() {
    const {pathname, search} = document.location;

    if (pathname.indexOf('/search') === 0 && search.indexOf('?q=') === 0) {
        update({'user_conduct_search': 'Yes'});
    }
};

function checkPdp() {
    if (isPageType(PAGE_TYPE.PDP)) {
        const pk = SV.get('item.pk');
        trackEvent('pdp_view', {pk});
    }
};

checkAbandonCheckout();
checkConductSearch();
checkPdp();

module.exports = chat;



// WEBPACK FOOTER //
// ./assets/js/global/utils/social/chat.es.js